import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout6Root } from './Layout6Root';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { FeaturedListingCard } from './FeaturedListingCard';
import { InventoryFilterBar } from './InventoryFilterBar';
import { InventoryFiltersDrawer } from './InventoryFiltersDrawer';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowUpDown } from 'lucide-react';
import { useAppData, useFilteredVehicles } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import {
  buildDefaultFilters,
  filterVehicles,
  filtersToSearchParams,
  hasActiveFilters,
  hasAdvancedFilters,
  sortVehicles,
  type FilterRanges,
  type VehicleFilterState,
} from './inventoryFilters';
import './inventory.css';
import './styles.css';

const DRAWER_HIDDEN_SECTIONS = ['type', 'brand', 'model'];

const VehiclesListing = () => {
  const { config } = useAppData();
  const { colors } = useTheme();
  const { selectedStore } = useStore();
  const contextVehicles = useFilteredVehicles(selectedStore);
  const allStores = config?.stores || [];
  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const titleColor = colors.title;
  const textColor = colors.text;
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [sortBy, setSortBy] = useState('price-asc');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    brand: true,
    year: true,
    price: true,
    mileage: true,
    fuel: true,
    category: true,
    transmission: true,
    color: true,
    optionals: true,
    type: true,
    store: true,
  });

  const [displayedCount, setDisplayedCount] = useState(12);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const itemsPerLoad = 12;
  const maxDisplayedItems = 100;
  const observerTarget = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const ranges = useMemo<FilterRanges>(() => {
    if (contextVehicles.length === 0) {
      return {
        minPrice: 0,
        maxPrice: 500000,
        minYear: 2000,
        maxYear: 2024,
        minMileage: 0,
        maxMileage: 200000,
      };
    }

    const minModelYear = Math.min(...contextVehicles.map((v) => v.modelYear));
    const maxModelYear = Math.max(...contextVehicles.map((v) => v.modelYear));

    return {
      minPrice: Math.min(...contextVehicles.map((v) => v.price)),
      maxPrice: Math.max(...contextVehicles.map((v) => v.price)),
      minYear: minModelYear - 1,
      maxYear: maxModelYear,
      minMileage: Math.min(...contextVehicles.map((v) => v.mileage)),
      maxMileage: Math.max(...contextVehicles.map((v) => v.mileage)),
    };
  }, [contextVehicles]);

  const [appliedFilters, setAppliedFilters] = useState<VehicleFilterState>(() =>
    buildDefaultFilters(ranges, searchParams)
  );
  const [pendingFilters, setPendingFilters] = useState<VehicleFilterState>(() =>
    buildDefaultFilters(ranges, searchParams)
  );

  const updatePending = useCallback((patch: Partial<VehicleFilterState>) => {
    setPendingFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const brands = useMemo(
    () => Array.from(new Set(contextVehicles.map((v) => v.brand))).sort(),
    [contextVehicles]
  );
  const models = useMemo(
    () =>
      pendingFilters.selectedBrand === 'all'
        ? Array.from(new Set(contextVehicles.map((v) => v.model))).sort()
        : Array.from(
            new Set(
              contextVehicles
                .filter((v) => v.brand === pendingFilters.selectedBrand)
                .map((v) => v.model)
            )
          ).sort(),
    [contextVehicles, pendingFilters.selectedBrand]
  );
  const fuels = useMemo(
    () => Array.from(new Set(contextVehicles.map((v) => v.fuel))).sort(),
    [contextVehicles]
  );
  const transmissions = useMemo(
    () => Array.from(new Set(contextVehicles.map((v) => v.transmission))).sort(),
    [contextVehicles]
  );
  const vehicleColors = useMemo(
    () => Array.from(new Set(contextVehicles.map((v) => v.color))).sort(),
    [contextVehicles]
  );
  const optionals = useMemo(() => {
    const allOptionals = contextVehicles.flatMap((v) => v.optionals || []);
    return Array.from(new Set(allOptionals)).sort();
  }, [contextVehicles]);
  const categories = useMemo(() => {
    const uniqueTypes = Array.from(new Set(contextVehicles.map((v) => v.type)));
    return uniqueTypes
      .map((type) => {
        switch (type) {
          case 1:
            return { value: '1', label: 'Carro' };
          case 2:
            return { value: '2', label: 'Moto' };
          case 3:
            return { value: '3', label: 'Caminhão' };
          default:
            return null;
        }
      })
      .filter(Boolean)
      .sort((a, b) => a!.value.localeCompare(b!.value)) as { value: string; label: string }[];
  }, [contextVehicles]);

  useEffect(() => {
    setAppliedFilters((prev) => ({
      ...prev,
      priceRange: [
        Math.max(prev.priceRange[0], ranges.minPrice),
        Math.min(prev.priceRange[1], ranges.maxPrice),
      ],
      yearRange: [
        Math.max(prev.yearRange[0], ranges.minYear),
        Math.min(prev.yearRange[1], ranges.maxYear),
      ],
      mileageRange: [
        Math.max(prev.mileageRange[0], ranges.minMileage),
        Math.min(prev.mileageRange[1], ranges.maxMileage),
      ],
    }));
    setPendingFilters((prev) => ({
      ...prev,
      priceRange: [
        Math.max(prev.priceRange[0], ranges.minPrice),
        Math.min(prev.priceRange[1], ranges.maxPrice),
      ],
      yearRange: [
        Math.max(prev.yearRange[0], ranges.minYear),
        Math.min(prev.yearRange[1], ranges.maxYear),
      ],
      mileageRange: [
        Math.max(prev.mileageRange[0], ranges.minMileage),
        Math.min(prev.mileageRange[1], ranges.maxMileage),
      ],
    }));
  }, [
    ranges.minPrice,
    ranges.maxPrice,
    ranges.minYear,
    ranges.maxYear,
    ranges.minMileage,
    ranges.maxMileage,
  ]);

  useEffect(() => {
    const fromUrl = buildDefaultFilters(ranges, searchParams);
    setAppliedFilters(fromUrl);
    setPendingFilters(fromUrl);
  }, [searchParams, ranges]);

  const previewVehicles = useMemo(
    () => sortVehicles(filterVehicles(contextVehicles, pendingFilters, searchParams, ranges), sortBy),
    [contextVehicles, pendingFilters, searchParams, ranges, sortBy]
  );

  const filteredVehicles = useMemo(
    () => sortVehicles(filterVehicles(contextVehicles, appliedFilters, searchParams, ranges), sortBy),
    [contextVehicles, appliedFilters, searchParams, ranges, sortBy]
  );

  useEffect(() => {
    setDisplayedCount(12);
    setIsLoadingMore(false);
  }, [filteredVehicles]);

  const loadMore = useCallback(() => {
    if (isLoadingMore) return;

    setIsLoadingMore(true);
    setDisplayedCount((prev) =>
      Math.min(prev + itemsPerLoad, filteredVehicles.length, maxDisplayedItems)
    );
    requestAnimationFrame(() => {
      setIsLoadingMore(false);
    });
  }, [isLoadingMore, filteredVehicles.length]);

  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    if (displayedCount >= Math.min(filteredVehicles.length, maxDisplayedItems)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(currentTarget);

    return () => {
      observer.unobserve(currentTarget);
    };
  }, [displayedCount, filteredVehicles.length, loadMore]);

  const clearPendingFilters = useCallback(() => {
    setPendingFilters(buildDefaultFilters(ranges));
  }, [ranges]);

  const clearAllFilters = useCallback(() => {
    const defaults = buildDefaultFilters(ranges);
    setAppliedFilters(defaults);
    setPendingFilters(defaults);
    setSearchParams(new URLSearchParams());
  }, [ranges, setSearchParams]);

  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...pendingFilters });
    const params = filtersToSearchParams(pendingFilters);
    setSearchParams(params.toString() ? params : new URLSearchParams());
    setDisplayedCount(12);
  }, [pendingFilters, setSearchParams]);

  const scrollToResultsIfNeeded = useCallback(() => {
    const resultsEl = resultsRef.current;
    if (!resultsEl) return;
    if (resultsEl.getBoundingClientRect().top > window.innerHeight * 0.4) {
      resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleApplyFromDrawer = useCallback(() => {
    setAppliedFilters({ ...pendingFilters });
    const params = filtersToSearchParams(pendingFilters);
    setSearchParams(params.toString() ? params : new URLSearchParams());
    setShowFiltersDrawer(false);
    setDisplayedCount(12);
    requestAnimationFrame(() => {
      scrollToResultsIfNeeded();
    });
  }, [pendingFilters, setSearchParams, scrollToResultsIfNeeded]);

  const handleDrawerOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setPendingFilters({ ...appliedFilters });
      } else {
        setPendingFilters({ ...appliedFilters });
      }
      setShowFiltersDrawer(open);
    },
    [appliedFilters]
  );

  const handleOpenMoreFilters = useCallback(() => {
    setPendingFilters({ ...appliedFilters });
    setShowFiltersDrawer(true);
  }, [appliedFilters]);

  const appliedHasActiveFilters = hasActiveFilters(appliedFilters, ranges);
  const pendingHasActiveFilters = hasActiveFilters(pendingFilters, ranges);
  const appliedHasAdvancedFilters = hasAdvancedFilters(appliedFilters, ranges);

  const displayedVehicles = filteredVehicles.slice(0, displayedCount);
  const totalCap = Math.min(filteredVehicles.length, maxDisplayedItems);
  const showingTo = Math.min(displayedCount, filteredVehicles.length);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const filterProps = {
    searchTerm: pendingFilters.searchTerm,
    setSearchTerm: (value: string) => updatePending({ searchTerm: value }),
    selectedBrand: pendingFilters.selectedBrand,
    setSelectedBrand: (value: string) => updatePending({ selectedBrand: value }),
    selectedModel: pendingFilters.selectedModel,
    setSelectedModel: (value: string) => updatePending({ selectedModel: value }),
    selectedFuels: pendingFilters.selectedFuels,
    setSelectedFuels: (value: string[]) => updatePending({ selectedFuels: value }),
    selectedTransmissions: pendingFilters.selectedTransmissions,
    setSelectedTransmissions: (value: string[]) => updatePending({ selectedTransmissions: value }),
    selectedType: pendingFilters.selectedType,
    setSelectedType: (value: string) => updatePending({ selectedType: value }),
    selectedCategory: pendingFilters.selectedCategory,
    setSelectedCategory: (value: string) => updatePending({ selectedCategory: value }),
    selectedColors: pendingFilters.selectedColors,
    setSelectedColors: (value: string[]) => updatePending({ selectedColors: value }),
    selectedOptionals: pendingFilters.selectedOptionals,
    setSelectedOptionals: (value: string[]) => updatePending({ selectedOptionals: value }),
    selectedStores: pendingFilters.selectedStores,
    setSelectedStores: (value: string[]) => updatePending({ selectedStores: value }),
    priceRange: pendingFilters.priceRange,
    setPriceRange: (value: number[]) => updatePending({ priceRange: value as [number, number] }),
    yearRange: pendingFilters.yearRange,
    setYearRange: (value: number[]) => updatePending({ yearRange: value as [number, number] }),
    mileageRange: pendingFilters.mileageRange,
    setMileageRange: (value: number[]) => updatePending({ mileageRange: value as [number, number] }),
    config,
    brands,
    models,
    fuels,
    transmissions,
    categories,
    colors: vehicleColors,
    optionals,
    availableStores: allStores,
    selectedStore,
    vehicles: contextVehicles,
    ranges,
    clearFilters: clearPendingFilters,
    hasActiveFilters: pendingHasActiveFilters,
    filteredCount: previewVehicles.length,
    expandedSections,
    toggleSection,
    hideHeader: true,
    hiddenSections: DRAWER_HIDDEN_SECTIONS,
    layout: 'drawer' as const,
  };

  return (
    <Layout6Root withHeader>
      <Header config={config} />

      <div className="wl6-inv-page">
        <div className="container mx-auto px-4 pt-8 pb-16">
          <div className="mb-8">
            <span className="l6-section-label">Estoque</span>
            <h1 className="l6-heading text-3xl md:text-4xl mt-3" style={{ color: titleColor }}>
              Veículos à venda
            </h1>
          </div>

          <InventoryFilterBar
              selectedType={pendingFilters.selectedType}
              setSelectedType={(value) => updatePending({ selectedType: value })}
              selectedBrand={pendingFilters.selectedBrand}
              setSelectedBrand={(value) => updatePending({ selectedBrand: value })}
              selectedModel={pendingFilters.selectedModel}
              setSelectedModel={(value) => updatePending({ selectedModel: value })}
              brands={brands}
              models={models}
              filteredCount={previewVehicles.length}
              hasAdvancedFilters={appliedHasAdvancedFilters}
              hasFilters={appliedHasActiveFilters || pendingHasActiveFilters}
              buttonColor={buttonColor}
              onOpenMoreFilters={handleOpenMoreFilters}
              onClear={clearAllFilters}
              onSearch={applyFilters}
            />

            <div ref={resultsRef} className="wl6-inv-results">
              <p className="wl6-inv-results__count">
                Mostrando <strong>1</strong> a <strong>{showingTo}</strong> de{' '}
                <strong>{filteredVehicles.length}</strong>{' '}
                {filteredVehicles.length === 1 ? 'veículo' : 'veículos'}
              </p>

              <div className="wl6-inv-results__sort">
                <span className="wl6-inv-results__sort-label">Ordenar por:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full sm:w-[200px] h-10">
                    <ArrowUpDown className="h-4 w-4 mr-2 shrink-0" />
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price-asc">Menor preço</SelectItem>
                    <SelectItem value="price-desc">Maior preço</SelectItem>
                    <SelectItem value="year-desc">Mais novo</SelectItem>
                    <SelectItem value="year-asc">Mais antigo</SelectItem>
                    <SelectItem value="mileage-asc">Menor quilometragem</SelectItem>
                    <SelectItem value="brand">Marca (A-Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              {filteredVehicles.length > 0 ? (
                <>
                  <div className="wl6-inv-grid">
                    {displayedVehicles.map((vehicle) => (
                      <div key={vehicle.id} className="wl6-inv-grid-item">
                        <FeaturedListingCard vehicle={vehicle} />
                      </div>
                    ))}
                  </div>

                  {displayedCount < totalCap && (
                    <div ref={observerTarget} className="wl6-inv-loading">
                      <div
                        className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
                        style={{
                          borderColor: `${primaryColor} transparent ${primaryColor} ${primaryColor}`,
                        }}
                      />
                      <p className="wl6-inv-loading__text">
                        Carregando mais veículos... ({displayedCount} de {totalCap})
                      </p>
                    </div>
                  )}

                  {displayedCount >= maxDisplayedItems &&
                  filteredVehicles.length > maxDisplayedItems ? (
                    <div className="wl6-inv-end">
                      <p className="wl6-inv-end__text">
                        Mostrando {maxDisplayedItems} de {filteredVehicles.length} veículos
                      </p>
                      <p className="wl6-inv-end__hint">
                        Use os filtros para refinar sua busca e ver resultados mais específicos
                      </p>
                    </div>
                  ) : displayedCount >= filteredVehicles.length && filteredVehicles.length > 12 ? (
                    <div className="wl6-inv-end">
                      <p className="wl6-inv-end__text">
                        Todos os {filteredVehicles.length} veículos foram carregados
                      </p>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="wl6-inv-empty">
                  <h2 className="wl6-inv-empty__title">Nenhum veículo encontrado</h2>
                  <p className="wl6-inv-empty__text">
                    Não encontramos veículos com os filtros selecionados. Tente ajustar sua busca.
                  </p>
                  <Button
                    onClick={clearAllFilters}
                    disabled={!appliedHasActiveFilters}
                    style={{ background: buttonColor }}
                  >
                    Limpar filtros
                  </Button>
                </div>
              )}
            </div>
        </div>
      </div>

      <InventoryFiltersDrawer
        open={showFiltersDrawer}
        onOpenChange={handleDrawerOpenChange}
        searchTerm={pendingFilters.searchTerm}
        onSearchTermChange={(value) => updatePending({ searchTerm: value })}
        previewCount={previewVehicles.length}
        buttonColor={buttonColor}
        hasPendingFilters={pendingHasActiveFilters}
        onClearPending={clearPendingFilters}
        onApply={handleApplyFromDrawer}
        filterProps={filterProps}
      />

      <Footer config={config} />
    </Layout6Root>
  );
};

export default VehiclesListing;
